/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Sparkles, Plus, Minus, ShoppingCart, Loader2, ArrowLeft, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { billetterieApi, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

function fmtEur(v) { return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €"; }
function fmtDateLong(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function BilletterieEvent() {
  useSeo({
    title: "Événement My Animation | Réserver mes billets",
    description: "Réservez vos places pour cet événement My Animation. Paiement 100% sécurisé, billet e-ticket envoyé par email.",
    path: "/billetterie",
  });
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // ticket_type_id → qty
  const [showCheckout, setShowCheckout] = useState(false);
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    billetterieApi.publicGet(slug)
      .then(setEvent)
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-[#DCB457]" /></div>;
  if (!event) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 text-center">
        <div>
          <h2 className="font-serif-luxe text-3xl mb-3">Événement introuvable.</h2>
          <Link to="/billetterie" className="btn-ghost-gold inline-flex">Voir tous les événements</Link>
        </div>
      </div>
    );
  }

  const setQty = (ttId, qty) => setCart((c) => {
    const v = Math.max(0, Math.min(20, qty));
    if (v === 0) { const { [ttId]: _, ...rest } = c; return rest; }
    return { ...c, [ttId]: v };
  });
  const inc = (tt) => setQty(tt.id, (cart[tt.id] || 0) + 1);
  const dec = (tt) => setQty(tt.id, (cart[tt.id] || 0) - 1);

  const items = Object.entries(cart).map(([ttId, qty]) => {
    const tt = event.ticket_types.find((x) => x.id === ttId);
    return { tt, qty, subtotal: (tt?.price_ttc || 0) * qty };
  });
  const total = items.reduce((s, it) => s + it.subtotal, 0);
  const totalQty = items.reduce((s, it) => s + it.qty, 0);

  const checkout = async (e) => {
    e.preventDefault();
    if (!buyer.buyer_email || !buyer.buyer_name) return toast.error("Nom et email requis.");
    if (totalQty === 0) return toast.error("Sélectionnez au moins un billet.");
    setSubmitting(true);
    try {
      const res = await billetterieApi.createOrder({
        event_slug: event.slug,
        items: Object.entries(cart).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity })),
        ...buyer,
        origin_url: window.location.origin,
      });
      // GA4 begin_checkout — juste avant redirect Stripe.
      try {
        trackEvent("begin_checkout", {
          currency: "EUR",
          value: total,
          items: items.map((it) => ({ item_id: it.ticket_type_id, item_name: it.name, quantity: it.qty, price: it.unit_price })),
          event_id: event.slug,
        });
      } catch (_) {}
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  const grad = event.bg_gradient || `linear-gradient(135deg, ${event.primary_color}, ${event.secondary_color})`;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="billetterie-event">
      {/* Hero coloré */}
      <section className="relative overflow-hidden min-h-[60vh] flex items-end">
        {!event.hero_no_overlay && <div className="absolute inset-0" style={{ background: grad }} />}
        {event.hero_no_overlay && <div className="absolute inset-0 bg-[#050505]" />}
        {event.hero_image_url && <img src={event.hero_image_url} alt={event.name} className={`absolute inset-0 w-full h-full object-cover ${event.hero_no_overlay ? "opacity-100" : "opacity-50"}`} />}
        {!event.hero_no_overlay && <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />}
        {event.hero_no_overlay && <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent pointer-events-none" />}
        <div className="absolute top-6 left-6 z-10">
          <Link to="/billetterie" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-xs uppercase tracking-[0.18em] bg-black/50 backdrop-blur px-3 py-1.5 rounded">
            <ArrowLeft size={11} /> Tous les événements
          </Link>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16 pt-32 w-full">
          {event.tagline && (
            <p className="inline-flex items-center gap-2 text-[#dcb457] text-xs uppercase tracking-[0.25em] mb-4">
              <Sparkles size={12} /> {event.tagline}
            </p>
          )}
          <h1 className="font-serif-luxe text-5xl md:text-7xl leading-[1.05] drop-shadow-2xl">{event.name}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-white/90">
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {fmtDateLong(event.date)}{event.time && ` · ${event.time}`}</span>
            {(event.venue_name || event.city) && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {event.venue_name}{event.city && `, ${event.city}`}</span>}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {event.description && (
            <article className="prose-blog">
              <p className="text-white/85 leading-relaxed whitespace-pre-line">{event.description}</p>
            </article>
          )}

          <div>
            <h2 className="font-serif-luxe text-3xl mb-5">Choisissez vos billets</h2>
            <div className="space-y-3">
              {(event.ticket_types || []).filter((tt) => tt.is_active !== false).map((tt) => {
                const qty = cart[tt.id] || 0;
                const soldOut = tt.remaining === 0;
                return (
                  <article key={tt.id} data-testid={`tt-${tt.id}`} className="relative overflow-hidden border border-white/15 hover:border-white/40 transition-all p-5">
                    <div className="absolute top-0 left-0 w-1.5 h-full" style={{ background: tt.color }} />
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <h3 className="font-serif-luxe text-2xl" style={{ color: tt.color }}>{tt.name}</h3>
                        {tt.description && <p className="text-white/65 text-sm mt-1">{tt.description}</p>}
                        {tt.remaining !== null && !soldOut && tt.remaining <= 20 && <p className="text-amber-300 text-xs mt-2">Plus que {tt.remaining} disponible(s) !</p>}
                        {soldOut && <p className="text-red-300 text-xs mt-2 uppercase tracking-[0.18em]">Épuisé</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-serif-luxe text-3xl text-white">{fmtEur(tt.price_ttc)}</p>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">TTC</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => dec(tt)} disabled={qty === 0 || soldOut} data-testid={`tt-${tt.id}-dec`} className="w-9 h-9 border border-white/30 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"><Minus size={14} /></button>
                        <input type="number" min="0" max="20" value={qty} onChange={(e) => setQty(tt.id, Number(e.target.value) || 0)} className="w-12 text-center bg-transparent border border-white/30 px-1 py-1.5 text-white tabular-nums" data-testid={`tt-${tt.id}-qty`} />
                        <button onClick={() => inc(tt)} disabled={soldOut} data-testid={`tt-${tt.id}-inc`} className="w-9 h-9 border flex items-center justify-center hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed" style={{ borderColor: tt.color, color: tt.color }}><Plus size={14} /></button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>

        {/* Aside: sticky cart */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="border border-white/15 p-6 bg-black/40 backdrop-blur">
            <h3 className="font-serif-luxe text-2xl mb-4 inline-flex items-center gap-2"><ShoppingCart size={18} /> Panier</h3>
            {items.length === 0 ? (
              <p className="text-white/55 text-sm italic">Aucun billet sélectionné.</p>
            ) : (
              <>
                <ul className="space-y-2 mb-4">
                  {items.map(({ tt, qty, subtotal }) => (
                    <li key={tt.id} className="flex justify-between text-sm">
                      <span className="text-white/85">{qty}× <span style={{ color: tt.color }}>{tt.name}</span></span>
                      <span className="text-white">{fmtEur(subtotal)}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-white/15 pt-3 flex items-baseline justify-between">
                  <span className="text-white/65 text-sm">Total</span>
                  <span className="font-serif-luxe text-3xl text-white">{fmtEur(total)}</span>
                </div>
                <button onClick={() => setShowCheckout(true)} data-testid="checkout-open" className="w-full mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 text-black font-medium uppercase tracking-[0.18em] text-sm hover:opacity-90 transition" style={{ background: grad }}>
                  <Lock size={14} /> Acheter
                </button>
                <p className="text-[10px] text-white/45 mt-3 inline-flex items-start gap-1.5"><Lock size={10} className="mt-0.5" /> Paiement sécurisé par Stripe. Vos billets vous seront envoyés par email après le paiement.</p>
              </>
            )}
          </div>
        </aside>
      </section>

      {/* Checkout modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowCheckout(false)}>
          <form onSubmit={checkout} className="border border-white/20 bg-[#0a0a0a] p-6 w-full max-w-md">
            <h2 className="font-serif-luxe text-3xl mb-2">Vos coordonnées</h2>
            <p className="text-white/55 text-xs mb-5">Vos billets vous seront envoyés à cette adresse.</p>
            <div className="space-y-3">
              <input className="input-luxe" placeholder="Nom complet *" required value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} data-testid="checkout-name" />
              <input className="input-luxe" type="email" placeholder="Email *" required value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value.toLowerCase() })} data-testid="checkout-email" />
              <input className="input-luxe" placeholder="Téléphone (optionnel)" value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })} />
            </div>
            <div className="mt-5 p-3 bg-white/5 border border-white/10">
              <div className="flex justify-between text-sm"><span className="text-white/65">Total</span><span className="text-white font-medium">{fmtEur(total)}</span></div>
            </div>
            <button type="submit" disabled={submitting} data-testid="checkout-pay" className="w-full mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 text-black font-medium uppercase tracking-[0.18em] text-sm disabled:opacity-60" style={{ background: grad }}>
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Payer par carte
            </button>
            <button type="button" onClick={() => setShowCheckout(false)} className="w-full mt-2 text-white/55 text-xs uppercase tracking-[0.18em] py-2">Annuler</button>
          </form>
        </div>
      )}
    </div>
  );
}
